import { useState, useCallback, useEffect, useRef } from "react"
import { useDispatch } from "react-redux"
import {
  getAIDashboardKpiData,
  getAIDashboardWidgets,
  getAIDashboardWidgetData,
  updateAIDashboardWidget,
  saveAIDashboardState,
  getAIDashboardState,
  dataRequested,
  dataReceived,
  kpiRequested,
  kpiReceived
} from "store/aiDashboard"
import { KPI_KEYS, WIDGET_LAYOUT, CHART_TITLE_TO_WIDGET_ID, PERIOD_MAP } from "../constants"
import { buildPeriodParams, resolveSavedPeriod } from "../utils"

export default function useDashboardData() {
  const dispatch = useDispatch()
  const widgetApiIdsRef = useRef([])

  const [widgets, setWidgets] = useState([])
  const [dashboardData, setDashboardData] = useState(null)
  const [kpiData, setKpiData] = useState({})
  const [activeDateFilter, setActiveDateFilter] = useState("This month")
  const [dateRange, setDateRange] = useState([null, null])
  const [singleDate, setSingleDate] = useState(null)
  const [hasNonDefault, setHasNonDefault] = useState(false)

  const fetchAllKpis = useCallback((period, range, single) => {
    const params = buildPeriodParams(period, range, single)
    dispatch(kpiRequested())
    Promise.all(
      KPI_KEYS.map((kpi) =>
        dispatch(getAIDashboardKpiData(kpi, params))
          .then((res) => ({ kpi, data: res }))
          .catch(() => ({ kpi, data: null }))
      )
    ).then((results) => {
      const map = {}
      results.forEach(({ kpi, data }) => { map[kpi] = data })
      setKpiData(map)
    }).finally(() => dispatch(kpiReceived()))
  }, [dispatch])

  const fetchWidgetWithData = useCallback((w, params) => {
    if (!w.id) return Promise.resolve({ widget: w, data: null })
    const { period, ...widgetParams } = params
    return dispatch(getAIDashboardWidgetData(w.id, widgetParams))
      .then((res) => ({ widget: w, data: res }))
      .catch(() => ({ widget: w, data: null }))
  }, [dispatch])

  const fetchDashboardData = useCallback((period, range, single) => {
    const params = buildPeriodParams(period, range, single)
    dispatch(dataRequested())
    dispatch(getAIDashboardWidgets(params))
      .then((res) => {
        let widgetsArray = res?.widgets ?? []

        widgetsArray.sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        const defaultCount = widgetsArray.filter((w) => w.is_default === true).length
        setHasNonDefault(widgetsArray.some((w) => w.is_default === false) || defaultCount < 6)

        widgetApiIdsRef.current = widgetsArray.filter((w) => w.id).map((w) => w.id)

        return Promise.all(widgetsArray.map((w) => fetchWidgetWithData(w, params)))
      })
      .then((results) => {
        const widgetsMap = {}
        const newWidgets = []
        results.forEach(({ data }) => {
          if (!data) return
          const localId = CHART_TITLE_TO_WIDGET_ID[data.chart_title]
          if (localId) {
            widgetsMap[localId] = { chart_data: data.chart_data ?? null, group_by: data.group_by ?? null, chart_type: data.chart_type ?? null, chart_title: data.chart_title ?? null }
            newWidgets.push({ id: localId, apiId: data.widget_id, ...(WIDGET_LAYOUT[localId] ?? { colSpan: 1, rowSpan: 1 }) })
          } else if (data.widget_id) {
            const dynamicId = `dynamic-${data.widget_id}`
            widgetsMap[dynamicId] = data
            newWidgets.push({ id: dynamicId, apiId: data.widget_id, colSpan: 1, rowSpan: 1, type: "api-dynamic" })
          }
        })
        setWidgets(newWidgets)
        setDashboardData(widgetsMap)
      })
      .catch(() => setDashboardData(null))
      .finally(() => dispatch(dataReceived()))
  }, [dispatch, fetchWidgetWithData])

  const handlePeriodChange = useCallback(async (period, range, single) => {
    const timePeriod = PERIOD_MAP[period] || "last_30_days"
    const apiIds = widgetApiIdsRef.current
    let dateRangeStr
    if (single) {
      dateRangeStr = single.format("YYYY-MM-DD")
    } else if (range?.[0] && range?.[1]) {
      dateRangeStr = `${range[0].format("YYYY-MM-DD")}T${range[1].format("YYYY-MM-DD")}`
    }

    const widgetPayload = { time_period: timePeriod }
    if (dateRangeStr) widgetPayload.date_range = dateRangeStr
    const statePayload = { global_period: timePeriod }
    if (dateRangeStr) statePayload.date_range = dateRangeStr

    if (apiIds.length > 0) {
      await Promise.all(apiIds.map((id) => dispatch(updateAIDashboardWidget(id, widgetPayload)).catch(() => {})))
    }
    try {
      await dispatch(saveAIDashboardState(statePayload))
    } catch (err) {
      console.error("Failed to save dashboard state:", err)
    }
    fetchDashboardData(period, range, single)
    fetchAllKpis(period, range, single)
  }, [dispatch, fetchDashboardData, fetchAllKpis])

  useEffect(() => {
    const contentEl = document.querySelector(".ant-layout-content")
    const bg = getComputedStyle(document.documentElement).getPropertyValue("--color-surface5").trim()
    if (contentEl) contentEl.style.background = bg
    return () => { if (contentEl) contentEl.style.background = "" }
  }, [])

  useEffect(() => {
    dispatch(getAIDashboardState())
      .then((res) => {
        const resolved = resolveSavedPeriod(res?.state)
        if (resolved.type === "range") {
          setActiveDateFilter(resolved.label)
          setDateRange(resolved.range)
          fetchDashboardData(resolved.label, resolved.range, null)
          fetchAllKpis(resolved.label, resolved.range, null)
        } else if (resolved.type === "single") {
          setActiveDateFilter(resolved.label)
          setSingleDate(resolved.single)
          fetchDashboardData(resolved.label, null, resolved.single)
          fetchAllKpis(resolved.label, null, resolved.single)
        } else {
          setActiveDateFilter(resolved.label)
          fetchDashboardData(resolved.label)
          fetchAllKpis(resolved.label)
        }
      })
      .catch(() => {
        fetchDashboardData("This month")
        fetchAllKpis("This month")
      })
  }, [dispatch, fetchDashboardData, fetchAllKpis])

  return {
    widgets, setWidgets,
    dashboardData,
    kpiData,
    activeDateFilter, setActiveDateFilter,
    dateRange, setDateRange,
    singleDate, setSingleDate,
    hasNonDefault,
    widgetApiIdsRef,
    fetchDashboardData,
    fetchAllKpis,
    handlePeriodChange
  }
}
